import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api, callableErrorMessage } from "../lib/api";
import { InfoButton } from "./InfoButton";

type Props = {
  tenantId: string;
  initialCategories: string[];
  initialRewardIcon: string;
};

const EMOJI_PALETTE = [
  "☕", "🥐", "🍵", "🥤", "🧉", "🍰", "🍪", "🍫",
  "🥪", "🍔", "🍕", "🍝", "🥗", "🌮", "🍣", "🍦",
  "🛍", "🎁", "💄", "🪴", "✂", "💅"
];

const DEFAULT_CATEGORIES = ["Cafeteria", "Comida", "Bebida", "Panaderia", "Producto", "Servicio", "Otro"];

export function OperationalConfigEditor({ tenantId, initialCategories, initialRewardIcon }: Props) {
  const [ambConfig, setAmbConfig] = useState<{
    voucherTtlMinutes: number;
    postUseBlockHours: number;
    defaultDiscountPercent: number;
    defaultCommissionRate: number;
    commissionCapMonthlyClp: number | null;
  }>({
    voucherTtlMinutes: 30,
    postUseBlockHours: 24,
    defaultDiscountPercent: 10,
    defaultCommissionRate: 0.1,
    commissionCapMonthlyClp: null
  });
  const [categories, setCategories] = useState<string[]>(
    initialCategories.length > 0 ? initialCategories : DEFAULT_CATEGORIES
  );
  const [rewardIcon, setRewardIcon] = useState(initialRewardIcon || "☕");
  const [capEnabled, setCapEnabled] = useState(initialCategories ? false : false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    return onSnapshot(doc(db, "tenants", tenantId, "config", "ambassador"), (snap) => {
      const d = snap.data();
      if (!d) return;
      setAmbConfig((prev) => ({
        voucherTtlMinutes: d.voucherTtlMinutes ?? prev.voucherTtlMinutes,
        postUseBlockHours: d.postUseBlockHours ?? prev.postUseBlockHours,
        defaultDiscountPercent: d.defaultDiscountPercent ?? prev.defaultDiscountPercent,
        defaultCommissionRate: d.defaultCommissionRate ?? prev.defaultCommissionRate,
        commissionCapMonthlyClp: d.commissionCapMonthlyClp ?? null
      }));
      setCapEnabled(d.commissionCapMonthlyClp != null);
    });
  }, [tenantId]);

  const commissionPct = useMemo(() => Math.round(ambConfig.defaultCommissionRate * 100), [ambConfig]);

  const updateAmb = <K extends keyof typeof ambConfig>(key: K, value: typeof ambConfig[K]) => {
    setAmbConfig((prev) => ({ ...prev, [key]: value }));
  };

  const saveAll = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      await Promise.all([
        api.updateTenantAmbassadorConfig({
          tenantId,
          config: {
            voucherTtlMinutes: ambConfig.voucherTtlMinutes,
            postUseBlockHours: ambConfig.postUseBlockHours,
            defaultDiscountPercent: ambConfig.defaultDiscountPercent,
            defaultCommissionRate: ambConfig.defaultCommissionRate,
            commissionCapMonthlyClp: capEnabled ? ambConfig.commissionCapMonthlyClp ?? 0 : null
          }
        }),
        api.updateTenantCategories({ tenantId, categories }),
        api.updateTenantLoyaltyConfig({ tenantId, config: { rewardIcon } })
      ]);
      setFeedback("✓ Guardado");
      setTimeout(() => setFeedback(null), 2500);
    } catch (err) {
      setFeedback(`✗ ${callableErrorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <Block
        title="Icono del reward"
        hint="Aparece en la loyalty card del cliente."
        info={<p>El icono se muestra en la card del cliente cada vez que suma una visita y al canjear el reward. Eligilo en linea con tu marca.</p>}
      >
        <div className="grid grid-cols-8 gap-2">
          {EMOJI_PALETTE.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setRewardIcon(e)}
              className={`grid h-10 w-10 place-items-center rounded-xl text-xl transition ${
                rewardIcon === e ? "bg-leaf-700 ring-2 ring-leaf-900" : "bg-leaf-50 hover:bg-leaf-100"
              }`}
            >
              {e}
            </button>
          ))}
        </div>
      </Block>

      <Block
        title="Categorias de venta"
        hint="Lo que el cajero elige al cobrar. Hasta 12 items."
        info={
          <p>
            Cuando el cajero registra una venta, elige a que categoria pertenece (Cafe, Comida,
            Bebida, etc). Despues podes filtrar tus ventas por categoria en el tab Ventas para
            saber que se vende mas. Editable cuando quieras.
          </p>
        }
      >
        <CategoryEditor categories={categories} onChange={setCategories} />
      </Block>

      <Block
        title="Descuento y comision por defecto"
        hint="Se aplica a embajadores nuevos."
        info={
          <p>
            Cuando aprobas un embajador nuevo, su codigo arranca con estos valores. Despues
            podes ajustar cada codigo individualmente. % descuento es lo que recibe el cliente
            que usa el codigo. % comision es lo que vos pagas al embajador sobre el neto
            (post-IVA, post-descuento) de cada venta.
          </p>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <NumberInput
            label="% descuento"
            value={ambConfig.defaultDiscountPercent}
            onChange={(v) => updateAmb("defaultDiscountPercent", v)}
            min={0}
            max={50}
            suffix="%"
          />
          <NumberInput
            label="% comision"
            value={commissionPct}
            onChange={(v) => updateAmb("defaultCommissionRate", v / 100)}
            min={0}
            max={50}
            suffix="%"
          />
        </div>
      </Block>

      <Block
        title="Tiempo de vida del QR"
        hint="Cuanto vive el voucher tras activarse antes del cobro."
        info={
          <p>
            Cuando un cliente activa un codigo de descuento, el sistema genera un voucher
            personal. El voucher (y el QR) caducan despues de este tiempo. Default 30 min.
            Si es muy corto, el cliente puede perderlo en cola. Si es muy largo, ocupa lugar.
          </p>
        }
      >
        <NumberInput
          label="Minutos"
          value={ambConfig.voucherTtlMinutes}
          onChange={(v) => updateAmb("voucherTtlMinutes", v)}
          min={5}
          max={240}
          suffix="min"
        />
      </Block>

      <Block
        title="Bloqueo post-uso"
        hint="Tras usar descuento, cuanto espera el cliente para usar otro."
        info={
          <p>
            Despues de que un cliente uso un descuento en tu local, no puede activar otro
            codigo del mismo embajador hasta que pase este tiempo. Evita que un cliente
            abuse del descuento con multiples compras chicas. Setea 0 si queres permitir
            descuento en cada compra.
          </p>
        }
      >
        <NumberInput
          label="Horas"
          value={ambConfig.postUseBlockHours}
          onChange={(v) => updateAmb("postUseBlockHours", v)}
          min={0}
          max={720}
          suffix="h"
        />
        <p className="text-[11px] text-leaf-500">0 = sin bloqueo (puede usar descuento cada compra).</p>
      </Block>

      <Block
        title="Cap mensual de comisiones por embajador"
        hint="Limite de pago al mes a cada embajador."
        info={
          <p>
            Maximo en CLP que vas a pagar de comision a cada embajador en un mes calendario.
            Cuando un embajador alcanza el cap, las comisiones siguientes en ese mes se
            registran pero con valor recortado. El cap se reinicia el dia 1 de cada mes.
            Util si tenes embajadores con muchos seguidores que podrian generar mas
            comision de la que queres pagar.
          </p>
        }
      >
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={capEnabled}
            onChange={(e) => setCapEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-leaf-300"
          />
          Activar cap mensual
        </label>
        {capEnabled && (
          <NumberInput
            label="Cap CLP"
            value={ambConfig.commissionCapMonthlyClp ?? 100000}
            onChange={(v) => updateAmb("commissionCapMonthlyClp", v)}
            min={0}
            max={100_000_000}
            suffix="CLP"
            step={1000}
          />
        )}
      </Block>

      {feedback && (
        <p
          className={`rounded-2xl px-4 py-3 text-sm ${
            feedback.startsWith("✓") ? "bg-leaf-50 text-leaf-800" : "bg-red-50 text-red-700"
          }`}
        >
          {feedback}
        </p>
      )}

      <button onClick={saveAll} disabled={busy} className="btn-primary">
        {busy ? "Guardando…" : "Guardar cambios"}
      </button>
    </div>
  );
}

function Block({ title, hint, info, children }: { title: string; hint?: string; info?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 border-t border-leaf-100 pt-4 first:border-0 first:pt-0">
      <div>
        <p className="flex items-center gap-1.5 text-sm font-medium text-leaf-900">
          {title}
          {info && <InfoButton>{info}</InfoButton>}
        </p>
        {hint && <p className="text-[11px] text-leaf-500">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  suffix
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-leaf-700">
      <span>{label}</span>
      <div className="flex items-center rounded-xl bg-leaf-50 ring-1 ring-leaf-200 focus-within:ring-leaf-500">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={min}
          max={max}
          step={step ?? 1}
          inputMode="numeric"
          className="w-full bg-transparent px-3 py-2 text-sm font-medium text-leaf-950 outline-none"
        />
        {suffix && <span className="pr-3 text-xs text-leaf-500">{suffix}</span>}
      </div>
    </label>
  );
}

function CategoryEditor({
  categories,
  onChange
}: {
  categories: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (categories.includes(v)) return;
    if (categories.length >= 12) return;
    onChange([...categories, v]);
    setDraft("");
  };

  const remove = (idx: number) => {
    onChange(categories.filter((_, i) => i !== idx));
  };

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-wrap gap-2">
        {categories.map((c, i) => (
          <li
            key={`${c}-${i}`}
            className="inline-flex items-center gap-1 rounded-full bg-leaf-50 px-3 py-1 text-xs text-leaf-800 ring-1 ring-leaf-200"
          >
            <span>{c}</span>
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-leaf-500 hover:text-red-600"
              aria-label={`Quitar ${c}`}
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Agregar categoria"
          maxLength={30}
          className="input flex-1"
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim() || categories.length >= 12}
          className="btn-secondary px-4 disabled:opacity-50"
        >
          +
        </button>
      </div>
      <p className="text-[11px] text-leaf-500">
        {categories.length}/12 categorias.
      </p>
    </div>
  );
}
